<template>
  <div class="home-dashboard-hub">
    <b-card v-if="showTabNav" no-body class="home-dashboard-tabs-card mb-2">
      <b-card-body class="py-1 px-1">
        <b-nav pills class="home-dashboard-tabs">
          <b-nav-item
            v-if="showAdminDashboardTab"
            :active="activeTab === TAB_DASHBOARD"
            @click="setTab(TAB_DASHBOARD)"
          >
            <feather-icon icon="HomeIcon" size="16" class="mr-50" />
            Dashboard
          </b-nav-item>
          <b-nav-item
            v-if="showCrmDashboardTab"
            :active="activeTab === TAB_CRM"
            @click="setTab(TAB_CRM)"
          >
            <feather-icon icon="BarChart2Icon" size="16" class="mr-50" />
            CRM Dashboard
          </b-nav-item>
          <b-nav-item
            v-if="showCpDashboardTab"
            :active="activeTab === TAB_CRM_DASHBOARD"
            @click="setTab(TAB_CRM_DASHBOARD)"
          >
            <feather-icon icon="UsersIcon" size="16" class="mr-50" />
            CP Dashboard
          </b-nav-item>
        </b-nav>
      </b-card-body>
    </b-card>

    <div class="home-dashboard-panel">
      <analytics-dashboard
        v-if="activeTab === TAB_DASHBOARD"
        :key="'main-dashboard'"
      />
      <crm-management-dashboard
        v-else-if="activeTab === TAB_CRM"
        :key="'crm-management'"
      />
      <crm-dashboard
        v-else-if="activeTab === TAB_CRM_DASHBOARD"
        :key="'crm-dashboard-panel'"
      />
    </div>
  </div>
</template>

<script>
import { BCard, BCardBody, BNav, BNavItem } from "bootstrap-vue";
import AnalyticsDashboard from "@/views/dashboard/analytics/Analytics.vue";
import CrmManagementDashboard from "@/views/crm/CrmManagementDashboard.vue";
import CrmDashboard from "@/views/crm/CrmDashboard.vue";

const TAB_DASHBOARD = "dashboard";
const TAB_CRM = "crm";
const TAB_CRM_DASHBOARD = "crm-dashboard";
const STORAGE_KEY = "homeDashboardActiveTab";

const ALL_TABS = [TAB_DASHBOARD, TAB_CRM, TAB_CRM_DASHBOARD];

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}");
  } catch (e) {
    return {};
  }
}

function isChannelPartnerUser(user) {
  const role = String(user?.role || "").toLowerCase();
  const type = String(user?.type || "").toLowerCase();
  return role === "channelpartner" || type === "channelpartner";
}

function defaultTabForUser(user) {
  return isChannelPartnerUser(user) ? TAB_CRM_DASHBOARD : TAB_DASHBOARD;
}

function allowedTabsForUser(user) {
  if (isChannelPartnerUser(user)) {
    return [TAB_CRM_DASHBOARD];
  }
  return ALL_TABS;
}

function normalizeTabForUser(tab, user) {
  const allowed = allowedTabsForUser(user);
  if (allowed.includes(tab)) {
    return tab;
  }
  return defaultTabForUser(user);
}

export default {
  name: "HomeDashboardHub",
  components: {
    BCard,
    BCardBody,
    BNav,
    BNavItem,
    AnalyticsDashboard,
    CrmManagementDashboard,
    CrmDashboard,
  },
  data() {
    const user = readUserData();
    return {
      TAB_DASHBOARD,
      TAB_CRM,
      TAB_CRM_DASHBOARD,
      user,
      activeTab: defaultTabForUser(user),
    };
  },

  computed: {
    isChannelPartner() {
      return isChannelPartnerUser(this.user);
    },
    allowedTabs() {
      return allowedTabsForUser(this.user);
    },
    showTabNav() {
      return this.allowedTabs.length > 1;
    },
    showAdminDashboardTab() {
      return this.allowedTabs.includes(TAB_DASHBOARD);
    },
    showCrmDashboardTab() {
      return this.allowedTabs.includes(TAB_CRM);
    },
    showCpDashboardTab() {
      return this.allowedTabs.includes(TAB_CRM_DASHBOARD);
    },
  },
  watch: {
    "$route.query.tab": {
      immediate: true,
      handler() {
        this.syncTabFromRoute();
      },
    },
  },
  mounted() {
    this.user = readUserData();
    this.syncTabFromRoute();
    this.ensureDefaultRouteQuery();
  },
  methods: {
    syncTabFromRoute() {
      this.user = readUserData();
      const routeTab = (this.$route.query.tab || "").toString().toLowerCase();
      let tab = routeTab;
      if (!tab) {
        const stored = localStorage.getItem(STORAGE_KEY);
        tab = ALL_TABS.includes(stored) ? stored : "";
      }
      this.activeTab = normalizeTabForUser(tab || defaultTabForUser(this.user), this.user);
    },
    ensureDefaultRouteQuery() {
      const routeTab = (this.$route.query.tab || "").toString().toLowerCase();
      const allowed = allowedTabsForUser(this.user);
      if (routeTab && !allowed.includes(routeTab)) {
        this.redirectToTab(this.activeTab);
        return;
      }
      if (this.$route.path !== "/") {
        return;
      }
      const query = this.tabQuery(this.activeTab);
      if (JSON.stringify(this.$route.query) === JSON.stringify(query)) {
        return;
      }
      this.$router.replace({ path: "/", query }).catch(() => {});
    },
    tabQuery(tab) {
      if (tab === TAB_CRM) {
        return { tab: TAB_CRM };
      }
      if (tab === TAB_CRM_DASHBOARD) {
        return { tab: TAB_CRM_DASHBOARD };
      }
      return {};
    },
    redirectToTab(tab) {
      const normalized = normalizeTabForUser(tab, this.user);
      this.activeTab = normalized;
      localStorage.setItem(STORAGE_KEY, normalized);
      const query = this.tabQuery(normalized);
      if (this.$route.path === "/" && JSON.stringify(this.$route.query) === JSON.stringify(query)) {
        return;
      }
      this.$router.replace({ path: "/", query }).catch(() => {});
    },
    setTab(tab) {
      const normalized = normalizeTabForUser(tab, this.user);
      if (this.activeTab === normalized) {
        return;
      }
      this.activeTab = normalized;

      localStorage.setItem(STORAGE_KEY, normalized);
      const query = this.tabQuery(normalized);
      if (this.$route.path === "/" && JSON.stringify(this.$route.query) === JSON.stringify(query)) {
        return;
      }
      this.$router.replace({ path: "/", query }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.home-dashboard-tabs-card {
  border-radius: 0.428rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
}

.home-dashboard-tabs >>> .nav-link {
  font-weight: 600;
  padding: 0.6rem 1.25rem;
  border-radius: 0.358rem;
}

.home-dashboard-tabs >>> .nav-item {
  margin-right: 0.35rem;
}

.home-dashboard-panel {
  min-height: 200px;
}
</style>
